import { isAddress, parseAbi, zeroAddress, type Address, type Hex } from 'viem';
import { z } from 'zod';
import { experimentalTriggerOrderAbi } from './conditionalOrderReceipt.js';

// Exact experimental version, not a production account/module profile.
export const experimentalLinkedAllocationAbi = [...experimentalTriggerOrderAbi, ...parseAbi([
  'function registerAllocation(uint128 maximumInput,(uint128 minimumTotalOutput,uint16 maximumFeeBps,uint256 expiry,(uint256 threshold,bool atOrBelow,bool latchAfterFirstFill) trigger) first,(uint128 minimumTotalOutput,uint16 maximumFeeBps,uint256 expiry,(uint256 threshold,bool atOrBelow,bool latchAfterFirstFill) trigger) second) returns(uint256 firstId,uint256 secondId)',
  'function allocationPeer(uint256) view returns(uint256)',
  'function allocationRemaining(uint256 id) view returns(uint128)',
])] as const;

const uint256 = z.bigint().min(0n).max((1n << 256n) - 1n);
const positive256 = uint256.refine(value => value > 0n);
const uint128 = z.bigint().min(0n).max((1n << 128n) - 1n);
const positive128 = uint128.refine(value => value > 0n);
const address = z.custom<Address>(value => typeof value === 'string'
  && isAddress(value, { strict: false }) && value.toLowerCase() !== zeroAddress);
const hash = z.custom<Hex>(value => typeof value === 'string' && /^0x[0-9a-f]{64}$/i.test(value));
const leg = z.object({
  id: positive256, peer: positive256, epoch: positive256, owner: address, buy: z.literal(false),
  maximumInput: positive128, minimumTotalOutput: positive128,
  maximumFeeBps: z.number().int().min(0).max(10_000), expiry: uint256,
  cancelled: z.boolean(), filled: z.boolean(), cumulativeInput: uint128,
  cumulativeOutput: uint256, fillRevision: uint256, observedRemaining: uint128,
}).strict();
const snapshot = z.object({
  version: z.literal('conditional-linked-allocation.1'), chainId: z.literal(4663), engine: address,
  blockNumber: uint256, blockHash: hash, first: leg, second: leg,
}).strict();
export type ExperimentalLinkedAllocationSnapshot = z.infer<typeof snapshot>;

/** Consistency/quantity projection only, never admission or execution authority.
 * Caller must read both orders, reciprocal links and both remaining getters at ONE
 * exact block from a separately verified engine. This pure function cannot establish
 * RPC authenticity, code pins, current owner/epoch, trigger eligibility or finality.
 * Cancellation does not restore group quantity. It does not imply peer cancellation.
 */
export function inspectExperimentalLinkedAllocation(input: ExperimentalLinkedAllocationSnapshot) {
  const s = snapshot.parse(input), a = s.first, b = s.second;
  if (a.id === b.id || a.peer !== b.id || b.peer !== a.id || a.epoch !== b.epoch
    || a.owner.toLowerCase() !== b.owner.toLowerCase() || a.maximumInput !== b.maximumInput)
    throw new Error('Inconsistent linked allocation identity');
  for (const order of [a, b]) {
    if (order.cumulativeInput > order.maximumInput
      || order.filled !== (order.cumulativeInput === order.maximumInput)
      || (order.cancelled && order.filled)
      || (order.cumulativeInput === 0n
        ? order.cumulativeOutput !== 0n || order.fillRevision !== 0n
        : order.cumulativeOutput === 0n || order.fillRevision === 0n || order.fillRevision > order.cumulativeInput
          || order.cumulativeOutput * order.maximumInput < order.cumulativeInput * order.minimumTotalOutput))
      throw new Error('Inconsistent linked allocation progress');
  }
  const consumed = a.cumulativeInput + b.cumulativeInput;
  if (consumed > a.maximumInput) throw new Error('Linked allocation overspent');
  const remaining = a.maximumInput - consumed;
  if (a.observedRemaining !== remaining || b.observedRemaining !== remaining)
    throw new Error('Linked allocation remaining disagrees with legs');
  return {
    version: s.version, chainId: s.chainId, engine: s.engine,
    blockNumber: s.blockNumber, blockHash: s.blockHash,
    orderIds: [a.id, b.id] as const, maximumInput: a.maximumInput, consumed, remaining,
    exhausted: remaining === 0n,
    // Alternative caps against ONE shared remainder, never additive budgets.
    // Nonzero does NOT mean a leg is eligible for execution.
    legQuantityCaps: [a.cancelled || a.filled ? 0n : remaining, b.cancelled || b.filled ? 0n : remaining] as const,
    executionAuthorized: false as const, accountingEligible: false as const,
    verification: 'same-block-code-authority-trigger-and-canonicality-required' as const,
  };
}
